/**
 * BLRRD Pipeline — LOD (Level-of-Detail) preview scheduler.
 *
 * Phase 3.B-perf P5. Solves the 8K-stutter problem: a 33-MP RAW (~528 MB
 * float32 RGBA) re-renders the whole kernel on every slider tweak, blowing
 * the 16 ms frame budget by 10x+. Lightroom / Capture One / darktable all
 * solve this with a permanent low-res preview cache + debounced full-res
 * render. We didn't have a clean seam for it before — this module is that
 * seam.
 *
 * Architecture (Halide-tile-pipeline-flavored, no code copy):
 *
 *   ┌───────────────┐  buildPreviewSource()   ┌───────────────────┐
 *   │  full source  │ ──────────────────────▶ │  preview source   │
 *   │  Float32 RGBA │   (canvas downsample,   │  Float32 RGBA     │
 *   │  W×H ACEScg   │    sRGB re-ingest)      │  ≤ 1440px / edge  │
 *   └───────────────┘                         └───────────────────┘
 *           │                                          │
 *           │  requestFull (debounced 400 ms)          │  requestPreview (sync, fast)
 *           ▼                                          ▼
 *   ┌──────────────────┐                        ┌──────────────────┐
 *   │  full-res render │ ◀─ cancellable ─       │ preview render   │
 *   │  100–500 ms wall │                        │ 5–15 ms wall     │
 *   └──────────────────┘                        └──────────────────┘
 *
 * Memory budget:
 *   • 1440 px × 810 px × 4ch × 4B = ~18 MB Float32 + ~5 MB Uint8 per source
 *   • One-time allocation at image load. Released on image close.
 *
 * Correctness contract:
 *   • Preview is a VIEW for live feedback only. NEVER used for export
 *     (LUT bake, save, batch). Exporters set E.lodTier = 'full' or call
 *     scheduler.cancel() + run synchronous full-res.
 *   • Snapshot apply / undo / redo always trigger requestFull (no preview
 *     latency added to those interactions).
 *   • Small images (longest edge ≤ maxDim) skip preview entirely. The
 *     pixel-hash + worker-parity tests run on small synthetic images and
 *     must never see the preview path.
 *
 * The module is pure ESM. The scheduler does not import any DOM / window
 * globals — the caller injects render functions. This keeps it unit-testable
 * and reusable from a worker context if we want a worker-driven LOD path
 * in 3.B-perf.1 follow-up.
 */

/* ───────────────────────── tunables ───────────────────────── */

/* Default longest-edge cap for the preview source. 1440 was picked to
 * exactly cover a typical 1440p MBP/external display 1:1 — there is no
 * visible benefit going higher. Keep this aligned with the test gate
 * assertions (`tests/.../test-lod-preview.mjs`). */
export const PREVIEW_MAX_DIM_DEFAULT = 1440;

/* Debounce window between last user input and the full-res render kick-off.
 * 400 ms balances "feels instant" (<500 ms is below conscious wait
 * threshold for most users) with "doesn't fire mid-drag" (most slider
 * drags settle within 150-300 ms of inactivity). Capture One uses ~300 ms,
 * Lightroom ~500 ms. */
export const FULL_RES_DEBOUNCE_MS_DEFAULT = 400;

/* ───────────────────── preview source builder ───────────────────── */

/**
 * Build a downsampled preview source from the original sRGB Uint8 buffer.
 *
 * Returns `null` when the source is already ≤ maxDim — caller should treat
 * this as "no preview needed, render full-res directly". Saves ~25 MB +
 * one ingest pass on small images, and keeps the existing pixel-hash
 * baselines (which run on tiny synthetic fixtures) untouched.
 *
 * @param {Uint8ClampedArray|Uint8Array} srcU8   sRGB-encoded RGBA, length = w*h*4
 * @param {number} w                              source width in pixels
 * @param {number} h                              source height in pixels
 * @param {number} [maxDim=1440]                  longest-edge cap for preview
 * @param {object} [helpers]                      injected dependencies
 * @param {Function} [helpers.ingestSRGB]         srcU8 → ACEScg Float32 (skip if absent → /255 fallback)
 * @param {Function} [helpers.allocFloat32]       Float32 allocator (defaults to plain new)
 * @param {Function} [helpers.allocUint8Clamped]  Uint8Clamped allocator (defaults to plain new)
 * @returns {{previewU8:Uint8ClampedArray, previewFloat:Float32Array,
 *            previewImgData: ImageData|null, w:number, h:number,
 *            scale:number}|null}
 */
export function buildPreviewSource(srcU8, w, h, maxDim, helpers) {
    const cap = (typeof maxDim === 'number' && maxDim > 0) ? maxDim : PREVIEW_MAX_DIM_DEFAULT;
    if (!srcU8 || !(w > 0) || !(h > 0)) return null;
    /* Small-image bypass: if longest edge already fits, callers should
     * skip preview entirely. Returning null is the explicit signal. */
    const longest = w >= h ? w : h;
    if (longest <= cap) return null;

    const scale = longest / cap;
    const pw = Math.max(1, Math.round(w / scale));
    const ph = Math.max(1, Math.round(h / scale));

    /* Canvas downsample is the most accurate option in-browser without
     * vendoring a Lanczos kernel. Browser uses a high-quality bilinear/
     * bicubic when imageSmoothingQuality='high'. The downsample happens
     * in sRGB-encoded space — ACEScg downsample would be more correct
     * for HDR but adds a 2x ingest cost on every preview rebuild and
     * the visual difference is negligible at 1440p preview-feedback
     * quality. */
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
        /* No DOM (worker / Node / SSR test) — caller must build preview
         * itself or skip. */
        return null;
    }
    /* Source canvas: full resolution. Use OffscreenCanvas when available
     * (Worker-safe + no layout cost) but DOM canvas works on every browser
     * the editor targets. */
    const srcCanvas = makeCanvas(w, h);
    const srcCtx = srcCanvas.getContext('2d');
    if (!srcCtx) return null;
    /* putImageData expects ImageData with sRGB-encoded RGBA — that's
     * exactly what srcU8 is, regardless of whether the original decode
     * was native PNG or a 16-bit TIFF quantized to 8-bit. */
    const srcImg = new ImageData(new Uint8ClampedArray(srcU8.buffer, srcU8.byteOffset, srcU8.byteLength), w, h);
    srcCtx.putImageData(srcImg, 0, 0);

    const dstCanvas = makeCanvas(pw, ph);
    const dstCtx = dstCanvas.getContext('2d');
    if (!dstCtx) return null;
    dstCtx.imageSmoothingEnabled = true;
    dstCtx.imageSmoothingQuality = 'high';
    dstCtx.drawImage(srcCanvas, 0, 0, pw, ph);
    const previewImgData = dstCtx.getImageData(0, 0, pw, ph);
    const pSrcU8 = previewImgData.data;
    const pLen = pSrcU8.length;

    const allocF32 = (helpers && helpers.allocFloat32)     || ((n) => new Float32Array(n));
    const allocU8C = (helpers && helpers.allocUint8Clamped) || ((n) => new Uint8ClampedArray(n));
    const previewU8    = allocU8C(pLen);
    previewU8.set(pSrcU8);
    const previewFloat = allocF32(pLen);

    if (helpers && typeof helpers.ingestSRGB === 'function') {
        /* Always ACEScg-ingest. The drag-preview feedback must match the
         * full-res view-transform output (otherwise hue/sat shifts visibly
         * on release). */
        helpers.ingestSRGB(pSrcU8, pw, ph, previewFloat);
    } else {
        /* Bootstrap not ready — /255 fallback keeps the editor functional
         * but the preview will look subtly different until ingestSRGB lands.
         * This branch only runs in the millisecond between bootstrap import
         * and module evaluation. */
        for (let j = 0; j < pLen; j++) previewFloat[j] = pSrcU8[j] / 255;
    }

    return {
        previewU8, previewFloat, previewImgData,
        w: pw, h: ph,
        scale,
    };
}

/**
 * Build a downsampled preview source DIRECTLY from a canonical Float32
 * ACEScg buffer — Phase 3.B-perf P5 fix (Codex Wave-2 STOP).
 *
 * Why this exists alongside `buildPreviewSource`: the sRGB-Uint8 path
 * downsamples a tone-mapped + clipped sidecar and re-ingests via
 * ingestSRGB. That works for native PNG/JPEG (where the U8 IS the
 * canonical source). It silently corrupts RAW / EXR / HDR-fused
 * sources — the canonical buffer is the Float32 (linear-Rec709 for
 * RAW, ACEScg for HDR fusion); going through a U8 round-trip:
 *
 *   1. clips to [0..255] (loses HDR > 1.0 highlights — drag preview
 *      shows blown-out white where full-res renders proper roll-off)
 *   2. applies 8-bit quantization (banding visible on 16-bit RAW
 *      gradients)
 *   3. for HDR-fused: re-tone-maps a tone-mapped image (double-
 *      filmic — visibly darker / hue-shifted preview)
 *
 * This builder area-resamples the Float32 buffer in scene-linear
 * directly. No lossy round-trip. The resulting `previewFloat` is in
 * the SAME color space as the input (callers must label correctly —
 * the legacy editor passes ACEScg-ingested Float32, so the preview
 * is also ACEScg).
 *
 * The U8 sidecar is built from `srcU8` (alpha pass-through) when
 * provided; if absent, derived from the float (clamped to [0..255]).
 *
 * @param {Float32Array}        srcFloat  ACEScg Float32 RGBA, length = w*h*4
 * @param {Uint8ClampedArray|Uint8Array|null} srcU8  alpha source (optional)
 * @param {number} w                              source width in pixels
 * @param {number} h                              source height in pixels
 * @param {number} [maxDim=1440]                  longest-edge cap for preview
 * @param {object} [helpers]                      injected dependencies
 * @param {Function} [helpers.allocFloat32]
 * @param {Function} [helpers.allocUint8Clamped]
 * @returns {{previewU8:Uint8ClampedArray, previewFloat:Float32Array,
 *            previewImgData: null, w:number, h:number, scale:number}|null}
 */
export function buildPreviewSourceFromFloat(srcFloat, srcU8, w, h, maxDim, helpers) {
    const cap = (typeof maxDim === 'number' && maxDim > 0) ? maxDim : PREVIEW_MAX_DIM_DEFAULT;
    if (!srcFloat || !(w > 0) || !(h > 0)) return null;
    if (srcFloat.length !== w * h * 4) return null;
    /* Small-image bypass: matches buildPreviewSource semantics. */
    const longest = w >= h ? w : h;
    if (longest <= cap) return null;

    const scale = longest / cap;
    const pw = Math.max(1, Math.round(w / scale));
    const ph = Math.max(1, Math.round(h / scale));

    const allocF32 = (helpers && helpers.allocFloat32)     || ((n) => new Float32Array(n));
    const allocU8C = (helpers && helpers.allocUint8Clamped) || ((n) => new Uint8ClampedArray(n));
    const previewFloat = allocF32(pw * ph * 4);
    const previewU8    = allocU8C(pw * ph * 4);

    const sx = w / pw;
    const sy = h / ph;
    const haveSrcU8 = (srcU8 && srcU8.length === w * h * 4);

    /* Area-resample (box-average over the source-pixel area each
     * destination pixel covers). For floats this is the canonical
     * downsample for HDR data — preserves total radiance under
     * Riemann-sum integration. Cost: O(w*h) per output, but bounded
     * by the integer floor/ceil step counts. */
    for (let y = 0; y < ph; y++) {
        const y0 = (y * sy) | 0;
        const y1raw = (y + 1) * sy;
        const y1 = Math.min(h, y1raw === (y1raw | 0) ? (y1raw | 0) : ((y1raw | 0) + 1));
        for (let x = 0; x < pw; x++) {
            const x0 = (x * sx) | 0;
            const x1raw = (x + 1) * sx;
            const x1 = Math.min(w, x1raw === (x1raw | 0) ? (x1raw | 0) : ((x1raw | 0) + 1));
            let r = 0, g = 0, b = 0, a = 0;
            let u8r = 0, u8g = 0, u8b = 0, u8a = 0;
            let count = 0;
            for (let yy = y0; yy < y1; yy++) {
                const rowBase = yy * w * 4;
                for (let xx = x0; xx < x1; xx++) {
                    const i = rowBase + xx * 4;
                    r += srcFloat[i];
                    g += srcFloat[i + 1];
                    b += srcFloat[i + 2];
                    a += srcFloat[i + 3];
                    if (haveSrcU8) {
                        u8r += srcU8[i];
                        u8g += srcU8[i + 1];
                        u8b += srcU8[i + 2];
                        u8a += srcU8[i + 3];
                    }
                    count++;
                }
            }
            const inv = count > 0 ? 1 / count : 1;
            const j = (y * pw + x) * 4;
            previewFloat[j]     = r * inv;
            previewFloat[j + 1] = g * inv;
            previewFloat[j + 2] = b * inv;
            previewFloat[j + 3] = a * inv;
            if (haveSrcU8) {
                previewU8[j]     = (u8r * inv + 0.5) | 0;
                previewU8[j + 1] = (u8g * inv + 0.5) | 0;
                previewU8[j + 2] = (u8b * inv + 0.5) | 0;
                previewU8[j + 3] = (u8a * inv + 0.5) | 0;
            } else {
                /* No U8 sidecar — derive from float, clamped. View-
                 * transform on the preview will produce the proper
                 * sidecar for canvas display anyway. */
                const fr = previewFloat[j];
                const fg = previewFloat[j + 1];
                const fb = previewFloat[j + 2];
                const fa = previewFloat[j + 3];
                previewU8[j]     = fr <= 0 ? 0 : fr >= 1 ? 255 : (fr * 255 + 0.5) | 0;
                previewU8[j + 1] = fg <= 0 ? 0 : fg >= 1 ? 255 : (fg * 255 + 0.5) | 0;
                previewU8[j + 2] = fb <= 0 ? 0 : fb >= 1 ? 255 : (fb * 255 + 0.5) | 0;
                previewU8[j + 3] = fa <= 0 ? 0 : fa >= 1 ? 255 : (fa * 255 + 0.5) | 0;
            }
        }
    }

    return {
        previewU8, previewFloat,
        previewImgData: null,   /* float path doesn't need an ImageData */
        w: pw, h: ph,
        scale,
    };
}

/**
 * Pick OffscreenCanvas if available, else DOM canvas. Both expose a
 * compatible 2D-context surface for our usage (drawImage, putImageData,
 * getImageData). Wrapped so it never throws — on platforms missing both,
 * the caller gets `null`.
 */
function makeCanvas(w, h) {
    try {
        if (typeof OffscreenCanvas === 'function') return new OffscreenCanvas(w, h);
    } catch { /* fall through */ }
    if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }
    return null;
}

/* ─────────────────────────── Scheduler ─────────────────────────── */

/**
 * Two-tier render scheduler.
 *
 * Lifecycle:
 *   • Construct once per editor session.
 *   • Caller wires `renderPreview` / `renderFull` callbacks at construction
 *     time. These are the actual "do the kernel pass" functions.
 *   • Each user interaction fires `requestPreview()` (cheap, sync), then
 *     `requestFull()` (debounced).
 *   • Snapshot apply / undo / redo / load fire `requestFull({immediate:true})`
 *     to skip the debounce.
 *   • Export / batch / LUT bake do NOT go through the scheduler at all —
 *     they call the editor's full-res render path directly with E.lodTier
 *     forced to 'full'. The scheduler is silent on those paths.
 *
 * Cancellation contract:
 *   • A pending full-res render (debounce timer running) is cancelled on
 *     any new requestPreview / requestFull. The new request reschedules.
 *   • An in-flight full-res render (callback already invoked, work in
 *     progress) cannot be true-cancelled in JS — kernels are synchronous
 *     CPU loops. The scheduler instead bumps a token; the worker-driven
 *     path checks the token before painting (existing stale-job guard
 *     already does this for the worker tier — we route through it).
 *   • For sync (main-thread) full-res, cancellation is best-effort: the
 *     in-flight render finishes, but its output is discarded if the token
 *     advanced. The next requestPreview will paint over it.
 */
export class LODScheduler {
    /**
     * @param {object} cfg
     * @param {() => void|Promise<void>}              cfg.renderPreview
     * @param {() => void|Promise<void>}              cfg.renderFull
     * @param {() => boolean}                         [cfg.hasPreview]   — returns true when preview source is built
     * @param {(tier:'preview'|'full') => void}       [cfg.onTierChange] — observability hook
     * @param {number}                                [cfg.debounceMs]   — defaults to FULL_RES_DEBOUNCE_MS_DEFAULT
     */
    constructor(cfg) {
        if (!cfg || typeof cfg.renderPreview !== 'function' || typeof cfg.renderFull !== 'function') {
            throw new Error('LODScheduler: cfg.renderPreview and cfg.renderFull required');
        }
        /* Use defensive copies of callable refs — callers don't get to
         * mutate them after construction. */
        const _preview     = cfg.renderPreview;
        const _full        = cfg.renderFull;
        const _hasPreview  = typeof cfg.hasPreview  === 'function' ? cfg.hasPreview  : () => true;
        const _onTier      = typeof cfg.onTierChange === 'function' ? cfg.onTierChange : () => {};
        const _debounceMs  = (typeof cfg.debounceMs === 'number' && cfg.debounceMs >= 0)
            ? cfg.debounceMs : FULL_RES_DEBOUNCE_MS_DEFAULT;

        /* All mutable state is encapsulated. No leaks via this.* —
         * scheduler stays a black box to the editor. */
        let _debounceTimer = null;
        let _fullToken     = 0;     /* monotonically increasing — bumped on every cancel */
        let _previewToken  = 0;     /* monotonic — bumped on cancel / immediate full to invalidate queued rAFs */
        let _previewQueued = false;
        let _currentTier   = 'idle';
        /* Region-invalidation state (Phase 4.F.polish.1 / Wave 12.2). Tri-state:
         *   • `undefined` — nothing pending (no requestFull in flight)
         *   • `null`      — whole-image render pending
         *   • `{x,y,w,h}` — region-only render pending
         * Distinguishing undefined-vs-null lets the scheduler know whether
         * "the next requestFull is the first one" — first wins, subsequent
         * unions / promotes. */
        let _pendingRegion = undefined;

        const setTier = (t) => {
            if (_currentTier !== t) {
                _currentTier = t;
                try { _onTier(t); } catch { /* observability must never throw */ }
            }
        };

        /* Coalesce repeated synchronous requestPreview calls into one
         * rAF tick so a slider drag at 60-120 Hz only renders once per
         * frame. Mirrors the existing `E.renderQueued` pattern.
         *
         * Critical correctness contract (Codex P5 review): every queued
         * rAF captures the current `_previewToken` and re-checks it at
         * flush time. `requestFull({immediate:true})` and `cancel()`
         * bump the token — so a snapshot-apply that fires AFTER a slider
         * tweak's rAF was queued but BEFORE it runs still wins, and the
         * stale preview never paints over the fresh full-res output. */
        const queuePreviewFlush = () => {
            const myToken = ++_previewToken;
            _previewQueued = true;
            const flush = () => {
                _previewQueued = false;
                if (myToken !== _previewToken) return;   /* invalidated */
                if (!_hasPreview()) {
                    /* No preview source available (small image, or load not
                     * complete) — promote to immediate full-res so the user
                     * still sees their edit. */
                    setTier('full');
                    _fullToken++;          /* invalidate any pending full job */
                    clearDebounce();
                    /* Wave 12.2 — snapshot + reset pending region before
                     * dispatch, mirroring the debounced `fire()` path.
                     * Without this, a small-image requestPreviewThenFull
                     * leaves `_pendingRegion` stale and subsequent
                     * region requests get incorrectly coalesced /
                     * promoted against it. Codex Wave-12.2 STOP-1 fix. */
                    const dispatchRegion = _pendingRegion;
                    _pendingRegion = undefined;
                    try {
                        if (dispatchRegion) {
                            _full(dispatchRegion);
                        } else {
                            _full();
                        }
                    } catch (e) { surfaceError(e); }
                    return;
                }
                setTier('preview');
                try { _preview(); } catch (e) { surfaceError(e); }
            };
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(flush);
            } else {
                setTimeout(flush, 0);
            }
        };

        const clearDebounce = () => {
            if (_debounceTimer !== null) {
                clearTimeout(_debounceTimer);
                _debounceTimer = null;
            }
        };

        /**
         * Schedule a preview render on the next frame. Idempotent within a
         * single frame — additional calls within the same rAF window are
         * coalesced via `_previewQueued`.
         */
        this.requestPreview = () => {
            if (_previewQueued) return;
            queuePreviewFlush();
        };

        /**
         * Schedule a debounced full-res render. Each call cancels the previous
         * pending one and starts a fresh timer. Pass `{immediate:true}` for
         * snapshot apply / undo / redo paths that need full-res NOW.
         *
         * Phase 4.F.polish.1 (Wave 12.2) — region-aware re-render:
         *   • `opts.region = {x, y, w, h}` (integer pixel coords) hints the
         *     renderer that ONLY this rectangle changed. Use case is paint
         *     strokes (Wave 9): a 100×100 brush dab on an 8K image should
         *     touch ~10k pixels, not 33 M. The renderer is responsible for
         *     honoring the hint — the scheduler just plumbs it through.
         *   • Default (no `region` arg) preserves existing whole-image
         *     semantics. All current call sites stay byte-identical.
         *   • Coalescing: when two region requests land before the debounce
         *     fires, they merge to the union AABB. This lets a fast paint
         *     stroke that ticks 20 region updates re-render once over the
         *     swept bbox instead of 20 times over each dab.
         *   • Immediate + region: also legal; the union accumulates only
         *     within a single debounce window — `immediate` flushes
         *     whatever's pending.
         *   • Mixing `region` with a subsequent `region:undefined` (or
         *     whole-image) request promotes the pending invalidation to
         *     whole-image — the safer fallback when intent is ambiguous.
         *
         * @param {object} [opts]
         * @param {boolean} [opts.immediate=false]  skip the debounce window
         * @param {number}  [opts.delayMs]          override debounce for this call
         * @param {{x:number,y:number,w:number,h:number}} [opts.region]
         *   optional dirty-rect hint. Coordinates are full-image pixel space
         *   (NOT preview). The scheduler validates `w > 0 && h > 0` and
         *   silently drops malformed regions (treated as whole-image).
         */
        this.requestFull = (opts) => {
            const immediate = !!(opts && opts.immediate);
            const delay = (opts && typeof opts.delayMs === 'number' && opts.delayMs >= 0)
                ? opts.delayMs : _debounceMs;
            /* Validate region. Anything malformed → null = whole-image. */
            const region = sanitizeRegion(opts && opts.region);

            /* Merge pending region with this request's region. Promotion
             * rules:
             *   • pending=null  + new=R    → R    (first region after whole)
             *   • pending=R1    + new=R2   → union(R1, R2)
             *   • pending=R     + new=null → null (whole-image wins)
             *   • pending=null  + new=null → null (whole-image stays)
             * This is conservative: any whole-image request promotes a
             * region-only pending to whole-image, which is the safe default
             * when intent is ambiguous. */
            if (_pendingRegion === undefined) {
                /* No pending request — adopt this one's region. */
                _pendingRegion = region;
            } else if (region === null) {
                /* New request is whole-image — promote. */
                _pendingRegion = null;
            } else if (_pendingRegion !== null) {
                /* Both regions — merge to union AABB. */
                _pendingRegion = unionRegion(_pendingRegion, region);
            }
            /* else: pending=null already, new=region — null wins (whole). */

            clearDebounce();
            _fullToken++;
            const myToken = _fullToken;
            /* Codex P5 fix — immediate full must invalidate any queued
             * preview rAF so a stale low-res frame doesn't paint over
             * the fresh full-res output. Token bump is enough; the
             * rAF callback re-checks before doing work. */
            if (immediate || delay === 0) {
                _previewToken++;
                _previewQueued = false;
            }
            const fire = () => {
                _debounceTimer = null;
                /* Token check guards against a race where another requestFull
                 * landed between schedule and fire. */
                if (myToken !== _fullToken) return;
                /* Snapshot + reset pending region BEFORE invoking the
                 * renderer — a renderer that synchronously calls
                 * requestFull (e.g. an error retry) must see a clean slate. */
                const dispatchRegion = _pendingRegion;
                _pendingRegion = undefined;
                setTier('full');
                try {
                    /* Pass region as second arg ONLY when non-null. This keeps
                     * the legacy 0-arg renderer signature byte-identical for
                     * whole-image renders — no behavioral drift on existing
                     * call sites (pixel-hash + worker-parity baselines). */
                    if (dispatchRegion) {
                        _full(dispatchRegion);
                    } else {
                        _full();
                    }
                } catch (e) { surfaceError(e); }
            };
            if (immediate || delay === 0) {
                fire();
            } else {
                _debounceTimer = setTimeout(fire, delay);
            }
        };

        /**
         * Convenience: render preview NOW, schedule full-res later. The
         * common slider-drag pattern. Forwards `opts` (including
         * `region`, `immediate`, `delayMs`) verbatim to requestFull.
         */
        this.requestPreviewThenFull = (opts) => {
            this.requestPreview();
            this.requestFull(opts);
        };

        /**
         * Cancel any pending full-res render. In-flight renders cannot be
         * synchronously aborted in JS — token bump prevents their output
         * from being painted (worker path) or autosaved (sync path).
         */
        this.cancel = () => {
            clearDebounce();
            _fullToken++;
            _previewToken++;             /* invalidate any rAF in flight */
            _previewQueued = false;
            _pendingRegion = undefined;  /* drop pending dirty-rect */
            setTier('idle');
        };

        /**
         * Read-only observability — primarily for the test gate.
         */
        this.getTier = () => _currentTier;
        this.getFullToken = () => _fullToken;
        this.getPreviewToken = () => _previewToken;
        this.isFullPending = () => _debounceTimer !== null;
        /* Wave 12.2: expose the pending region for the test gate + UI
         * observability. Returns `undefined` (nothing pending), `null`
         * (whole-image pending), or a snapshot copy of the region object
         * (so callers can't mutate scheduler state). */
        this.getPendingRegion = () => {
            if (_pendingRegion === undefined || _pendingRegion === null) {
                return _pendingRegion;
            }
            return { x: _pendingRegion.x, y: _pendingRegion.y,
                     w: _pendingRegion.w, h: _pendingRegion.h };
        };
    }
}

/* ─────────────────── Region helpers (Wave 12.2) ─────────────────── */

/**
 * Validate + normalize a region hint passed via `requestFull({region})`.
 * Returns `null` for malformed input (treated as whole-image) so the
 * scheduler doesn't crash on bad UI callers. Coordinates are floored to
 * integer pixel space — preview-lod is pixel-grid-aware, not subpixel.
 *
 * @param {unknown} region
 * @returns {{x:number,y:number,w:number,h:number}|null}
 */
export function sanitizeRegion(region) {
    if (!region || typeof region !== 'object') return null;
    const x = Number(region.x);
    const y = Number(region.y);
    const w = Number(region.w);
    const h = Number(region.h);
    if (!Number.isFinite(x) || !Number.isFinite(y) ||
        !Number.isFinite(w) || !Number.isFinite(h)) return null;
    if (w <= 0 || h <= 0) return null;
    return {
        x: Math.floor(x),
        y: Math.floor(y),
        w: Math.ceil(w),
        h: Math.ceil(h),
    };
}

/**
 * Axis-aligned union of two regions. Used to coalesce multiple
 * region-invalidation requests within a single debounce window.
 *
 * @param {{x:number,y:number,w:number,h:number}} a
 * @param {{x:number,y:number,w:number,h:number}} b
 * @returns {{x:number,y:number,w:number,h:number}}
 */
export function unionRegion(a, b) {
    const x0 = Math.min(a.x, b.x);
    const y0 = Math.min(a.y, b.y);
    const x1 = Math.max(a.x + a.w, b.x + b.w);
    const y1 = Math.max(a.y + a.h, b.y + b.h);
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/* In-process error surfacing — DON'T swallow exceptions silently. The
 * caller's render functions are expected to handle their own user-facing
 * error toasts. This is for unhandled programmer errors. */
function surfaceError(e) {
    /* eslint-disable-next-line no-console */
    if (typeof console !== 'undefined' && console.error) console.error('[LODScheduler]', e);
}
